import React from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import type { UserRole } from '@/types'

interface PermissionGateProps {
  children: React.ReactNode
  permission?: string
  permissions?: string[]
  role?: UserRole
  roles?: UserRole[]
  fallback?: React.ReactNode
  requireAll?: boolean
}

/**
 * Componente para controlar acesso baseado em permissões e roles
 * 
 * @param children - Conteúdo a ser exibido se o usuário tiver permissão
 * @param permission - Permissão específica necessária
 * @param permissions - Array de permissões necessárias
 * @param role - Role específico necessário
 * @param roles - Array de roles necessários
 * @param fallback - Conteúdo a ser exibido se o usuário não tiver permissão
 * @param requireAll - Se true, requer todas as permissões; se false, requer qualquer uma
 */
export function PermissionGate({
  children,
  permission,
  permissions,
  role,
  roles,
  fallback = null,
  requireAll = false
}: PermissionGateProps) {
  const { can, canAny, canAll, hasRole } = usePermissions()
  
  // Verificar permissões
  let hasPermission = true
  
  if (permission) {
    hasPermission = hasPermission && can(permission)
  }
  
  if (permissions && permissions.length > 0) {
    if (requireAll) {
      hasPermission = hasPermission && canAll(permissions)
    } else {
      hasPermission = hasPermission && canAny(permissions)
    }
  }
  
  // Verificar roles
  let hasRequiredRole = true
  
  if (role) {
    hasRequiredRole = hasRequiredRole && hasRole(role)
  }
  
  if (roles && roles.length > 0) {
    hasRequiredRole = hasRequiredRole && roles.some(r => hasRole(r))
  }
  
  const canAccess = hasPermission && hasRequiredRole
  
  if (!canAccess) {
    return <>{fallback}</>
  }
  
  return <>{children}</>
}

// Componentes de conveniência para casos comuns
export function AdminOnly({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  return (
    <PermissionGate role="administrador" fallback={fallback}>
      {children}
    </PermissionGate>
  )
}

export function VistoriaOnly({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  return (
    <PermissionGate roles={['administrador', 'vistoria']} fallback={fallback}>
      {children}
    </PermissionGate>
  )
}

export function CanManageVistorias({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  const { canManageVistorias } = usePermissions()
  
  if (!canManageVistorias()) {
    return <>{fallback}</>
  }
  
  return <>{children}</>
}

export function CanManageUsers({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  const { canManageUsers } = usePermissions()
  
  if (!canManageUsers()) {
    return <>{fallback}</>
  }
  
  return <>{children}</>
}

export function CanManageCalendarios({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  const { canManageCalendarios } = usePermissions()
  
  if (!canManageCalendarios()) {
    return <>{fallback}</>
  }
  
  return <>{children}</>
}

export function CanManageCardapios({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  const { canManageCardapios } = usePermissions()
  
  if (!canManageCardapios()) {
    return <>{fallback}</>
  }
  
  return <>{children}</>
}

export function CanManageGeolocation({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  const { canManageGeolocation } = usePermissions()
  
  if (!canManageGeolocation()) {
    return <>{fallback}</>
  }
  
  return <>{children}</>
}

export function CanManageParametros({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  const { canManageParametros } = usePermissions()
  
  if (!canManageParametros()) {
    return <>{fallback}</>
  }
  
  return <>{children}</>
}
